import dayjs from "dayjs";
import * as XLSX from "xlsx";

import { getCumplimiento } from "~/services/contabilidad/budgetVsActualService";

import type {
  BudgetVsActualGroupedData,
  BudgetVsActualValues,
} from "~/types/contabilidad";

interface BudgetVsActualExportData {
  data: BudgetVsActualGroupedData;
  year: number;
  month: number;
  companyName: string;
  templateName: string;
  budgetName: string;
}

const MONTH_NAMES = [
  "Enero",
  "Febrero",
  "Marzo",
  "Abril",
  "Mayo",
  "Junio",
  "Julio",
  "Agosto",
  "Septiembre",
  "Octubre",
  "Noviembre",
  "Diciembre",
];

function formatNumber(value: number): number {
  return Math.round(value);
}

function formatPct(value: number | null): string {
  return value === null ? "—" : value.toFixed(1) + "%";
}

// Ppto Mes | Real Mes | Desv Mes | % Mes | Ppto Acum | Real Acum | Desv Acum | % Acum
function valueColumns(values: BudgetVsActualValues): (string | number)[] {
  return [
    formatNumber(values.budgetMes),
    formatNumber(values.realMes),
    formatNumber(values.realMes - values.budgetMes),
    formatPct(getCumplimiento(values.realMes, values.budgetMes)),
    formatNumber(values.budgetAcumulado),
    formatNumber(values.realAcumulado),
    formatNumber(values.realAcumulado - values.budgetAcumulado),
    formatPct(getCumplimiento(values.realAcumulado, values.budgetAcumulado)),
  ];
}

const EMPTY_COLUMNS = ["", "", "", "", "", "", "", ""];

/**
 * Exports the Presupuesto vs Real report to Excel with both Mes and
 * Acumulado column blocks side by side.
 */
export const exportBudgetVsActualToExcel = ({
  data,
  year,
  month,
  companyName,
  templateName,
  budgetName,
}: BudgetVsActualExportData): void => {
  const workbook = XLSX.utils.book_new();

  const headerData: (string | number)[][] = [
    ["PRESUPUESTO VS REAL"],
    [],
    ["Empresa:", companyName],
    ["Año:", year],
    ["Mes:", MONTH_NAMES[month - 1]],
    ["Plantilla:", templateName],
    ["Presupuesto:", budgetName],
    ["Fecha de generación:", dayjs().format("DD/MM/YYYY HH:mm")],
    [],
    [
      "Descripción",
      "Ppto Mes",
      "Real Mes",
      "Desv Mes",
      "% Mes",
      "Ppto Acum",
      "Real Acum",
      "Desv Acum",
      "% Acum",
    ],
  ];

  const sheetRows: (string | number)[][] = [];

  for (const clase of data.clases) {
    sheetRows.push([clase.clase.toUpperCase(), ...EMPTY_COLUMNS]);

    for (const subClase of clase.subClases) {
      sheetRows.push(["  " + subClase.sub_clase, ...EMPTY_COLUMNS]);

      for (const subClase2 of subClase.subClases2) {
        sheetRows.push(["    " + subClase2.sub_clase_2, ...EMPTY_COLUMNS]);

        for (const grupo of subClase2.grupos) {
          if (grupo.grupo_cuenta_name) {
            const grupoLabel = grupo.grupo_cuenta_code
              ? `${grupo.grupo_cuenta_code} - ${grupo.grupo_cuenta_name}`
              : grupo.grupo_cuenta_name;
            sheetRows.push(["      " + grupoLabel, ...EMPTY_COLUMNS]);
          }

          for (const account of grupo.accounts) {
            const indent = grupo.grupo_cuenta_name ? "        " : "      ";
            sheetRows.push([
              `${indent}${account.account_code} - ${account.account_name}`,
              ...valueColumns(account),
            ]);
          }

          if (grupo.grupo_cuenta_name) {
            sheetRows.push([
              "      Total " + grupo.grupo_cuenta_name,
              ...valueColumns(grupo),
            ]);
          }
        }

        sheetRows.push([
          "    Total " + subClase2.sub_clase_2,
          ...valueColumns(subClase2),
        ]);
      }

      sheetRows.push([
        "  Total " + subClase.sub_clase,
        ...valueColumns(subClase),
      ]);
    }

    sheetRows.push([
      "TOTAL " + clase.clase.toUpperCase(),
      ...valueColumns(clase),
    ]);

    sheetRows.push(["", ...EMPTY_COLUMNS]);
  }

  // Summary section
  sheetRows.push(["", ...EMPTY_COLUMNS]);
  sheetRows.push(["RESUMEN", ...EMPTY_COLUMNS]);
  sheetRows.push(["Total Ingresos", ...valueColumns(data.totals.ingresos)]);
  sheetRows.push(["Total Costos", ...valueColumns(data.totals.costos)]);
  sheetRows.push([
    "Utilidad Bruta",
    ...valueColumns(data.totals.utilidadBruta),
  ]);
  sheetRows.push(["Total Gastos", ...valueColumns(data.totals.gastos)]);
  sheetRows.push([
    "Utilidad Operacional",
    ...valueColumns(data.totals.utilidadOperacional),
  ]);

  // Mismatch section: budgeted accounts missing from the template
  if (data.unmapped.length > 0) {
    sheetRows.push(["", ...EMPTY_COLUMNS]);
    sheetRows.push([
      "CUENTAS CON PRESUPUESTO SIN CLASIFICACIÓN EN LA PLANTILLA",
      ...EMPTY_COLUMNS,
    ]);
    for (const line of data.unmapped) {
      sheetRows.push([
        `${line.account_code} - ${line.account_name}`,
        formatNumber(line.budget_mes),
        formatNumber(line.real_mes),
        "",
        "",
        formatNumber(line.budget_acumulado),
        formatNumber(line.real_acumulado),
        "",
        "",
      ]);
    }
  }

  const sheetData = [...headerData, ...sheetRows];
  const worksheet = XLSX.utils.aoa_to_sheet(sheetData);

  worksheet["!cols"] = [
    { wch: 60 },
    { wch: 16 },
    { wch: 16 },
    { wch: 16 },
    { wch: 10 },
    { wch: 16 },
    { wch: 16 },
    { wch: 16 },
    { wch: 10 },
  ];

  XLSX.utils.book_append_sheet(workbook, worksheet, "Presupuesto vs Real");

  const monthName = MONTH_NAMES[month - 1].toLowerCase();
  const filename = `presupuesto_vs_real_${year}_${monthName}_${dayjs().format("YYYY-MM-DD")}.xlsx`;

  XLSX.writeFile(workbook, filename);
};
